import {
  fetchUserNotifications,
  subscribeToUserNotifications,
  updateNotificationAsRead,
} from "config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import React, { useState, useEffect, useRef } from "react";

interface NotificationItemProps {
  avatar: string;
  username: string;
  responderName: string;
  id: string;
  message: string;
  time: string;
  isNew: boolean;
}

interface NotificationDropdownProps {
  icon: string;
  buttonAltText?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  icon,
  buttonAltText = "Notifications",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItemProps[]>(
    []
  );
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const loggedIn = !!user;
      setIsUserLoggedIn(loggedIn);

      if (loggedIn && user) {
        const unsubscribeNotifications = subscribeToUserNotifications(
          user.uid,
          (fetchedNotifications) => {
            const mapped = fetchedNotifications.map((notif: any) => ({
              avatar: "",
              id: notif.id,
              username: notif.responderName || "Anonymous",
              responderName: notif.responderName || "Anonymous",
              message: notif.message || "",
              time: notif.createdAt?.toDate?.().toISOString() || "",
              isNew: !notif.IsRead,
            }));

            setNotifications(mapped);
            setUnreadCount(mapped.filter((n) => n.isNew).length);
          }
        );

        // Unsubscribe Firestore listener when user signs out
        return () => unsubscribeNotifications();
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchAndUpdateNotifications = async () => {
    if (!auth.currentUser) return;

    const fetchedNotifications = await fetchUserNotifications();
    if (fetchedNotifications && Array.isArray(fetchedNotifications)) {
      const mapped = fetchedNotifications.map((notif: any) => ({
        avatar: "",
        id: notif.id,
        username: notif.responderName || "Anonymous",
        responderName: notif.responderName || "Anonymous",
        message: notif.message,
        time: notif.createdAt?.toDate?.().toISOString() || "",
        isNew: !notif.IsRead,
      }));

      mapped.sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
      );
      setNotifications(mapped);

      // Update unread count
      const newUnreadCount = mapped.filter((n) => n.isNew).length;
      setUnreadCount(newUnreadCount);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Refresh notifications when opening dropdown
      fetchAndUpdateNotifications();
    }
  };

  const handleClickOutside = (event: PointerEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, []);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) return `${interval} year${interval > 1 ? "s" : ""} ago`;

    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) return `${interval} month${interval > 1 ? "s" : ""} ago`;

    interval = Math.floor(seconds / 86400);
    if (interval >= 1) return `${interval} day${interval > 1 ? "s" : ""} ago`;

    interval = Math.floor(seconds / 3600);
    if (interval >= 1) return `${interval} hour${interval > 1 ? "s" : ""} ago`;

    interval = Math.floor(seconds / 60);
    if (interval >= 1)
      return `${interval} minute${interval > 1 ? "s" : ""} ago`;

    return "Just now";
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) {
      return `${parts[0][0]}${parts[0].slice(-1)}`.toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const toggleExpand = async (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }

    const notification = notifications[index];
    if (notification.isNew && userId) {
      await updateNotificationAsRead(notification.id, userId);

      // Update local state
      const updatedNotifications = [...notifications];
      updatedNotifications[index] = { ...notification, isNew: false };
      setNotifications(updatedNotifications);

      // Update unread count
      //setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  return (
    <div className="relative items-center gap-2" ref={dropdownRef}>
      <button
        className="flex items-center justify-center w-10 h-10 rounded-full bg-[#0000001A] shadow-md hover:bg-[rgb(255,188,0)] transition"
        onClick={toggleDropdown}
      >
        <img className="w-5" src={icon} alt={buttonAltText} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 py-5 bg-white divide-y divide-gray-100 rounded-lg shadow-lg w-80 min-[482px]:right-0 max-[481px]:right-[-246px] max-[481px]:static max-[481px]:top-[47px] mt-1">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200 px-5">
            <p className="text-gray-800 text-sm font-semibold">Notifications</p>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[300px] overflow-y-auto px-4">
            {notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <div
                  key={index}
                  className="flex items-start py-3 border-b border-gray-100 last:border-none cursor-pointer"
                  onClick={() => toggleExpand(index)}
                >
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FFBC00] text-white text-sm font-bold">
                      {getInitials(notification.responderName)}
                    </div>
                  </div>

                  <div className="flex-1 ml-3">
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col">
                        <p className="text-gray-800 text-sm font-semibold">
                          {notification.responderName}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {expandedIndex === index
                            ? notification.message
                            : notification.message.length > 30
                              ? `${notification.message.substring(0, 30)}...`
                              : notification.message}
                        </p>
                        <p className="text-gray-400 text-[10px]">
                          {notification.time
                            ? formatTimeAgo(notification.time)
                            : ""}
                        </p>
                      </div>

                      <div
                        className={`flex items-center gap-2 ml-3 mt-3 transition-transform ${
                          expandedIndex === index ? "rotate-90" : ""
                        }`}
                      >
                        {notification.isNew && (
                          <span className="h-2 w-2 rounded-full bg-red-500"></span>
                        )}
                        <i className="st-arrow-right text-sm text-grease-200"></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-40">
                <p className="text-gray-400 text-sm">No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
